import {useMemo} from 'react';

import {isValidDate} from '../../../../util/date';
import {DateFromUTCString} from '../../../util/time';
import {type LogLabelFilterOption} from '../LogLabelFilter';
import {type LogLineData} from '../LogLineRenderer';
import {formatLogTimestamp, type LogTimestampFormatters} from '../logTimestamp';

function stripAnsiCodes(text: string): string {
  // eslint-disable-next-line no-control-regex
  return text.replace(/\u001b\[[0-9;]*m/g, '');
}

/**
 * useLogsFormattedForCopy formats log lines for clipboard copying, including
 * timestamps and labels based on visibility settings. It also strips ANSI color
 * codes for plain text output.
 */
export function useLogsFormattedForCopy(
  lines: readonly LogLineData[],
  labels: readonly LogLabelFilterOption[],
  isTimestampVisible: boolean,
  formatters: LogTimestampFormatters
) {
  return useMemo(() => {
    return lines
      .map(line => {
        const parts = [] as string[];

        if (isTimestampVisible) {
          const date = DateFromUTCString(line.timestamp);
          if (isValidDate(date)) {
            parts.push(formatLogTimestamp(date, formatters));
          }
        }

        if (labels.length > 0 && line.label) {
          parts.push(`[${line.label}]`);
        }

        parts.push(stripAnsiCodes(line.line));

        return parts.join(' ');
      })
      .join('\n');
  }, [lines, labels, isTimestampVisible, formatters]);
}
